import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, Loader2, Clock, MapPin, Coins, Lightbulb, Luggage, HandHeart, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const BUDGETS = [
  { v: "budget", l: "Budget / backpacker" },
  { v: "mid-range", l: "Mid-range comfort" },
  { v: "luxury", l: "Luxury / premium" },
];

export default function Planner() {
  const [cities, setCities] = useState([]);
  const [interests, setInterests] = useState([]);
  const [form, setForm] = useState({
    destinations: [], interests: [], budget: "mid-range",
    start_date: "", end_date: "", travelers: 2, notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    api.get("/data/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/data/interests").then((r) => setInterests(r.data)).catch(() => {});
  }, []);

  const toggle = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((x) => x !== value) : [...f[key], value],
    }));

  const generate = async () => {
    setLoading(true);
    setPlan(null);
    try {
      const { data } = await api.post("/itinerary/generate", form);
      setPlan(data.plan);
      toast.success("Your itinerary is ready!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-12 sm:px-10">
      <p className="overline text-primary">AI itinerary planner</p>
      <h1 className="mt-2 max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl">
        Tell us your dream trip. We'll craft the plan.
      </h1>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Form */}
        <div className="lg:col-span-5">
          <div className="space-y-7 rounded-2xl border bg-card p-7">
            <div>
              <Label className="mb-3 block">Where do you want to go?</Label>
              <div className="flex flex-wrap gap-2" data-testid="city-chips">
                {cities.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => toggle("destinations", c.name)}
                    data-testid={`city-${c.id}`}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                      form.destinations.includes(c.name)
                        ? "border-primary bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-3 block">What are you into?</Label>
              <div className="flex flex-wrap gap-2" data-testid="interest-chips">
                {interests.map((it) => (
                  <button
                    key={it}
                    onClick={() => toggle("interests", it)}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                      form.interests.includes(it)
                        ? "border-secondary bg-secondary text-secondary-foreground"
                        : "hover:bg-muted"
                    }`}
                  >
                    {it}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start">Start date</Label>
                <Input id="start" type="date" data-testid="start-date" value={form.start_date}
                  onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end">End date</Label>
                <Input id="end" type="date" data-testid="end-date" value={form.end_date}
                  onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} className="rounded-xl" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Budget</Label>
                <Select value={form.budget} onValueChange={(v) => setForm((f) => ({ ...f, budget: v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="budget-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BUDGETS.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="travelers">Travelers</Label>
                <Input id="travelers" type="number" min={1} data-testid="travelers-input" value={form.travelers}
                  onChange={(e) => setForm((f) => ({ ...f, travelers: Number(e.target.value) }))} className="rounded-xl" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Anything else?</Label>
              <Textarea id="notes" data-testid="notes-input" value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Traveling with kids, vegetarian, love live music…" className="min-h-[80px] rounded-xl" />
            </div>

            <Button onClick={generate} disabled={loading} className="w-full rounded-full" data-testid="generate-itinerary">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              {loading ? "Crafting your journey…" : "Generate itinerary"}
            </Button>
          </div>
        </div>

        {/* Result */}
        <div className="lg:col-span-7">
          {!plan && !loading && (
            <div className="flex h-full min-h-[400px] flex-col items-center justify-center rounded-2xl border border-dashed bg-card/40 p-10 text-center" data-testid="planner-placeholder">
              <MapPin className="h-10 w-10 text-muted-foreground" />
              <p className="mt-4 font-display text-xl">Your personalized plan appears here</p>
              <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                Choose your cities and interests, then let our AI build a day-by-day Morocco adventure.
              </p>
            </div>
          )}

          {loading && (
            <div className="flex h-full min-h-[400px] flex-col items-center justify-center rounded-2xl border bg-card p-10 text-center">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="mt-4 font-medium">Designing your authentic Moroccan itinerary…</p>
              <p className="mt-1 text-sm text-muted-foreground">This usually takes 15–30 seconds.</p>
            </div>
          )}

          {plan && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="itinerary-result">
              <div className="rounded-2xl border bg-card p-7">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="font-display text-2xl font-bold">{plan.title}</h2>
                  <Button variant="ghost" size="sm" onClick={() => setPlan(null)} className="rounded-full shrink-0">
                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> New
                  </Button>
                </div>
                <p className="mt-2 leading-relaxed text-muted-foreground">{plan.summary}</p>
                {plan.budget_estimate && (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent/20 px-4 py-1.5 text-sm font-medium">
                    <Coins className="h-4 w-4" /> {plan.budget_estimate}
                  </div>
                )}
              </div>

              {(plan.days || []).map((day, di) => (
                <motion.div
                  key={di}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: di * 0.05 }}
                  className="rounded-2xl border bg-card p-7"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                      {day.day || di + 1}
                    </span>
                    <h3 className="font-display text-xl font-semibold">{day.title}</h3>
                  </div>
                  <div className="mt-5 space-y-5 border-l-2 border-dashed border-border pl-5">
                    {(day.activities || []).map((act, ai) => (
                      <div key={ai} className="relative">
                        <span className="absolute -left-[26px] top-1 flex h-3 w-3 rounded-full bg-secondary ring-4 ring-background" />
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-secondary">
                            <Clock className="h-3 w-3" /> {act.time}
                          </span>
                          {act.estimated_cost && (
                            <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs">{act.estimated_cost}</span>
                          )}
                        </div>
                        <p className="mt-1 font-semibold">{act.name}</p>
                        <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">{act.description}</p>
                        {act.location && (
                          <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3" /> {act.location}
                          </p>
                        )}
                        {act.tip && (
                          <p className="mt-2 inline-flex items-start gap-1.5 rounded-lg bg-accent/10 px-3 py-1.5 text-xs text-foreground/80">
                            <Lightbulb className="mt-0.5 h-3 w-3 shrink-0 text-accent" /> {act.tip}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}

              <div className="grid gap-6 sm:grid-cols-2">
                {plan.packing_tips?.length > 0 && (
                  <div className="rounded-2xl border bg-card p-6">
                    <h4 className="flex items-center gap-2 font-display text-lg font-semibold"><Luggage className="h-5 w-5 text-primary" /> Packing tips</h4>
                    <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                      {plan.packing_tips.map((t, i) => <li key={i} className="flex gap-2"><span className="text-primary">•</span> {t}</li>)}
                    </ul>
                  </div>
                )}
                {plan.local_etiquette?.length > 0 && (
                  <div className="rounded-2xl border bg-card p-6">
                    <h4 className="flex items-center gap-2 font-display text-lg font-semibold"><HandHeart className="h-5 w-5 text-secondary" /> Local etiquette</h4>
                    <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                      {plan.local_etiquette.map((t, i) => <li key={i} className="flex gap-2"><span className="text-secondary">•</span> {t}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
